import dayjs from 'dayjs';
import { baseVariant, packVariant } from './Variants';
import { resolveDtcVariantPricing } from './dtcVariantPricing';

export const DTC_PACK_CONFIGS = {
  750: {
    type: 'Bottle',
    weight: '1200',
    barcodeCol: 'barcodeBaseBottle.750',
  },
  /** 375ml bottle — distinct from 375ml can (`375c`) for Shopify weights/barcode columns */
  '375b': {
    type: 'Bottle',
    weight: '500',
    barcodeCol: 'barcodeBaseBottle.375',
  },
  /** 375ml can (e.g. 24×375ml slab singles) */
  '375c': {
    type: 'Can',
    weight: '406',
    barcodeCol: 'barcodeBaseCan.375',
  },
  330: {
    type: 'Can',
    weight: '375',
    barcodeCol: 'barcodeBaseCan.330',
  },
  440: {
    type: 'Can',
    weight: '460',
    barcodeCol: 'barcodeBaseCan.440',
  },
  /** Keg litre keys aligned with barcode_keg.{20,30,50} / legacy modal */
  20: {
    type: 'Keg',
    weight: '28500',
    barcodeCol: 'barcodeKeg.20',
  },
  30: {
    type: 'Keg',
    weight: '42000',
    barcodeCol: 'barcodeKeg.30',
  },
  50: {
    type: 'Keg',
    weight: '65000',
    barcodeCol: 'barcodeKeg.50',
  },
};

/**
 * @param {string|number} unitSize
 * @param {'Can'|'Bottle'|'Keg'|undefined} formatPackType — from SKU-first builder; omit → 375 defaults to bottle (legacy)
 * @returns {string|number}
 */
export function resolveDtcPackConfigKey(unitSize, formatPackType) {
  const u = String(unitSize ?? '');
  if (formatPackType === 'Keg') return u;
  if (u === '375') {
    return formatPackType === 'Can' ? '375c' : '375b';
  }
  return unitSize;
}

function arrayToCommaSeparatedString(inputArray) {
  let productFilterArray;
  if (typeof inputArray === 'string') {
    try {
      productFilterArray = JSON.parse(inputArray);
    } catch (error) {
      console.error('Error parsing input string:', error);
      return '';
    }
  } else if (Array.isArray(inputArray)) {
    productFilterArray = inputArray;
  } else {
    console.error('Input is neither a string nor an array');
    return '';
  }
  return productFilterArray.filter((filter) => typeof filter === 'string').join(',');
}

/**
 * Build REST payload for POST /shopify/products/create.
 *
 * @param {object} args
 * @param {string} args.store — STORES.GPNZ | STORES.GPAU
 * @param {object} args.product — product row (camelCase)
 * @param {object} args.formData — unitSize, selectedRRP, SKU, productFilters, selectedSlab
 * @param {'Can'|'Bottle'|'Keg'} [args.formatPackType] — disambiguates 375ml can vs bottle (SKU-first); `'Keg'` for kegs
 * @param {Array} [args.dtcPrices] — from getDtcPricing
 * @param {object} [args.rules] — Pricing Guide rules (slab discounts)
 * @param {string|null} [args.unitBarcodeOverride] — resolved barcode (legacy + SKU); omit to use product columns only
 */
export function generateShopifyProductPayload({
  store,
  product,
  formData,
  formatPackType,
  dtcPrices,
  rules,
  unitBarcodeOverride,
}) {
  const packConfigs = DTC_PACK_CONFIGS;
  const packConfigKey = resolveDtcPackConfigKey(formData.unitSize, formatPackType);
  const fd = { ...formData, dtcPackConfigKey: packConfigKey };
  const { productFilters } = fd;
  const formattedProductFilters = arrayToCommaSeparatedString(productFilters);

  const packConfigEntry = packConfigs[packConfigKey];
  if (!packConfigEntry) {
    console.error('[generateShopifyProductPayload] Unknown pack config:', packConfigKey);
    throw new Error(`Unsupported Shopify pack format (${packConfigKey}).`);
  }

  const defaultSlab24 = fd.unitSize === '330' || packConfigKey === '375c';
  const customSlab = parseFloat(fd.selectedSlab || (defaultSlab24 ? '24' : '15'));
  const rrpSource = fd.selectedRRP || product.rrp;
  const floatedRRP = parseFloat(rrpSource);
  const {
    weight: packWeight,
    type: packType,
    barcodeCol,
  } = packConfigEntry;
  const baseWeight = parseFloat(packWeight);
  const trimmedOverride = unitBarcodeOverride != null ? String(unitBarcodeOverride).trim() : '';
  const unitBarcode = trimmedOverride !== ''
    ? trimmedOverride
    : product[barcodeCol];
  const { variants: pricedVariants } = resolveDtcVariantPricing({
    store,
    unitSize: fd.unitSize,
    packConfigKey,
    packType,
    unitPrice: floatedRRP,
    slabCount: customSlab,
    skus: product.skus ?? [],
    dtcPrices: dtcPrices ?? [],
    rules,
  });
  const formattedDate = product.dateReleased ? dayjs(product.dateReleased).format('MMMM YYYY') : '';

  const ingredients = () => {
    const { malts, hops } = product;

    const parseIngredients = (rawIngredients) => rawIngredients
      .replace(/\n/g, ', ')
      .replace(/,\s*,/g, ', ')
      .replace(/,\s*$/, '');

    const parsedHops = hops ? parseIngredients(hops) : '';
    const parsedMalts = malts ? parseIngredients(malts) : '';

    return parsedHops || parsedMalts
      ? `${parsedHops ? `${parsedHops} hops` : ''}${parsedHops && parsedMalts ? ', ' : ''}${parsedMalts ? `${parsedMalts} malt` : ''}`
      : '';
  };

  const oneLiner = (product.oneLiner || '').replace(/\n/g, '');

  const metafields = [
    {
      key: 'variety',
      value: oneLiner,
      type: 'single_line_text_field',
      namespace: 'global',
    },
    {
      key: 'style',
      value: oneLiner,
      type: 'single_line_text_field',
      namespace: 'global',
    },
    {
      key: 'percentage',
      value: `${product.abv}%`,
      type: 'single_line_text_field',
      namespace: 'global',
    },
    {
      key: 'brewed',
      value: ingredients(),
      type: 'single_line_text_field',
      namespace: 'global',
    },
    {
      key: 'artist',
      value: product.artist,
      type: 'single_line_text_field',
      namespace: 'global',
    },
    {
      key: 'release',
      value: formattedDate,
      type: 'single_line_text_field',
      namespace: 'global',
    },
    {
      key: 'bundled',
      value: true,
      type: 'single_line_text_field',
      namespace: 'global',
    },
    {
      key: 'coming_soon',
      value: true,
      type: 'single_line_text_field',
      namespace: 'global',
    },
    {
      key: 'filters',
      value: formattedProductFilters,
      type: 'single_line_text_field',
      namespace: 'global',
    },
  ];
  const variants = [
    baseVariant(fd, baseWeight, floatedRRP, packConfigs, unitBarcode),
  ];
  if (packType === 'Keg') {
    return {
      product: {
        title: product.name,
        body_html: product.blurb,
        vendor: 'Garage Project',
        status: 'draft',
        product_type: 'Keg',
        metafields,
        variants,
        options: [{
          name: 'Size',
        }],
      },
    };
  }
  pricedVariants
    .filter((row) => row.source !== 'unit' && row.count > 1)
    .forEach((row) => {
      variants.push(packVariant(row.title, row.count, fd, baseWeight, row.price, packConfigs));
    });

  return {
    product: {
      title: product.name,
      body_html: product.blurb,
      vendor: 'Garage Project',
      status: 'draft',
      product_type: 'Cans',
      metafields,
      variants,
      options: [{
        name: 'Size',
      }],
    },
  };
}
